import React, { useEffect } from "react";
// ANT DESIGN COMPONENTS
import { Card, Col, Descriptions, Row, Space, Tag, Typography } from "antd";
// ANT ICONS
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClusterOutlined,
  ApiOutlined,
} from "@ant-design/icons";
// FONT AWESOME LIBRARY AND ICONS
import { faBook } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
// INTERFACES
import { BankInterface } from "../../../interfaces/Bank.interface";
import { ChannelInterface } from "../../../interfaces/Channel.interface";
import { ServerInterface } from "../../../interfaces/Server.interface";
import { AdapterInterface } from "../../../interfaces/Adapter.interface";
import { OrganizationInterface } from "../../../interfaces/Organizations.interface";
// CONSTANTS
import { BLANK_CHANNEL } from "../../../constants/channels";
import { BLANK_SERVER } from "../../../constants/server";
import { BLANK_ADAPTER } from "../../../constants/adapter";
import { BLANK_ORGANIZATION } from "../../../constants/organization";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { liftTypeLabel, formatUuidT } from "../../../flatbuffers/display";

export default function BankDetailsCard({
  bank,
  isLoadingGetOneBank,
}: {
  bank: BankInterface;
  isLoadingGetOneBank: boolean;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const channel: ChannelInterface = bank?.channel ? bank.channel : BLANK_CHANNEL;
  const server: ServerInterface = bank?.channel?.server ? bank.channel.server : BLANK_SERVER;
  const adapter: AdapterInterface = bank?.adapter ? bank.adapter : BLANK_ADAPTER;
  const org: OrganizationInterface = bank?.channel?.server?.org
    ? bank.channel.server.org
    : BLANK_ORGANIZATION;
  const { Text } = Typography;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const yesNoTag = (value: boolean, yesLabel = "Yes", noLabel = "No") =>
    value ? (
      <Tag icon={<CheckCircleOutlined />} color="success">
        {yesLabel}
      </Tag>
    ) : (
      <Tag icon={<CloseCircleOutlined />} color="default">
        {noLabel}
      </Tag>
    );

  const safeText = (value: any, fallback = "—") => {
    if (value === null || value === undefined || value === "") return fallback;
    return String(value);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {}, []);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Card
      loading={isLoadingGetOneBank}
      title={
        <Space size={12}>
          <div>
            <Space size={8} wrap>
              <Tag color="cyan">{safeText(bank.name)}</Tag>
              {bank.active ? (
                <Tag color="green">{safeText("Active")}</Tag>
              ) : (
                <Tag color="red">{safeText("Inactive")}</Tag>
              )}
              {bank.type === LiftType.ELEVATOR && (
                <Tag color="blue">{liftTypeLabel(bank.type)}</Tag>
              )}
              {bank.type === LiftType.ESCALATOR && (
                <Tag color="volcano">{liftTypeLabel(bank.type)}</Tag>
              )}
              {bank.type === LiftType.MOVINGWALK && (
                <Tag color="green">{liftTypeLabel(bank.type)}</Tag>
              )}
              {bank.type === LiftType.ELEVATOR && (
                <> {yesNoTag(bank.rearDoors, "Rear doors", "No rear doors")}</>
              )}
            </Space>
          </div>
        </Space>
      }
      bordered
    >
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <div className="overviewGrid">
            <div className="generalStyles__mrFix mb-3">
              <DrawerRowDivider icon={faBook} title="Overview" />
              <Descriptions bordered size="small" column={1} labelStyle={{ width: 170 }}>
                <Descriptions.Item label="Bank ID" className="generalStyles__textEllipsis">
                  <div className="generalStyles__textEllipsis">{safeText(bank.id)}</div>
                </Descriptions.Item>
                <Descriptions.Item label="Channel ID">
                  <div className="generalStyles__textEllipsis">
                    {safeText(formatUuidT(bank.channelId))}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Logical Port">
                  {safeText(bank.logicalPort)}
                </Descriptions.Item>
                <Descriptions.Item label="Stops">{safeText(bank.stops)}</Descriptions.Item>
                <Descriptions.Item label="Floor Count">
                  {safeText(bank.floorCount)}
                </Descriptions.Item>
                <Descriptions.Item label="Start Floor No.">
                  {safeText(bank.startFloorNo)}
                </Descriptions.Item>
                <Descriptions.Item label="Below Lobby Floors">
                  {safeText(bank.floorBelowLobby)}
                </Descriptions.Item>
              </Descriptions>
            </div>
            <div className="generalStyles__mrFix mb-3 ">
              <DrawerRowDivider icon={faBook} title="Channel" />
              <Descriptions bordered size="small" column={1} labelStyle={{ width: 170 }}>
                <Descriptions.Item label="ID">
                  <Space>
                    <ClusterOutlined />
                    {safeText(channel?.id)}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Name">
                  <Space>
                    <ClusterOutlined />
                    {safeText(channel?.name)}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Connection">
                  {channel?.connectionType ? <Tag color="cyan">{channel.connectionType}</Tag> : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Routable Socket Address">
                  {safeText(channel?.routableSocketAddress)}
                </Descriptions.Item>
                <Descriptions.Item label="Reporting Socket">
                  {safeText(channel?.reportingSocketAddress)}
                </Descriptions.Item>
                <Descriptions.Item label="Vendor Socket">
                  {safeText(channel?.vendorSocketAddress)}
                </Descriptions.Item>
                <Descriptions.Item label="Heartbeat (ms)">
                  {safeText(channel?.heartbeatFrequency)}
                </Descriptions.Item>
                <Descriptions.Item label="Baud Rate">
                  {safeText(channel?.baudRate)}
                </Descriptions.Item>
                <Descriptions.Item label="Active">
                  {yesNoTag(!!channel?.active, "Yes", "No")}
                </Descriptions.Item>
              </Descriptions>
            </div>
            <div className="generalStyles__mrFix mb-3">
              <DrawerRowDivider icon={faBook} title="Server" />
              <Descriptions bordered size="small" column={1} labelStyle={{ width: 170 }}>
                <Descriptions.Item label="ID">
                  <Space>
                    <ApiOutlined />
                    {safeText(server?.id)}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Name">
                  <Space>
                    <ApiOutlined />
                    {safeText(server?.name)}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="IP">{safeText(server?.ip)}</Descriptions.Item>
                <Descriptions.Item label="Sync Schedule">
                  {safeText(server?.syncSched)}
                </Descriptions.Item>
                <Descriptions.Item label="Quest DB Sync">
                  {safeText(server?.syncQuestDbSched)}
                </Descriptions.Item>
                <Descriptions.Item label="Record Sync Size">
                  {safeText(server?.recordSyncSize)}
                </Descriptions.Item>
                <Descriptions.Item label="FB Enabled">
                  {yesNoTag(!!server?.fbEnabled, "Yes", "No")}
                </Descriptions.Item>
                <Descriptions.Item label="Active">
                  {yesNoTag(!!server?.active, "Yes", "No")}
                </Descriptions.Item>
                <Descriptions.Item label="Organization">
                  {org ? (
                    <Space direction="vertical" size={0}>
                      <Text strong>{safeText(org.name)}</Text>
                      <Text type="secondary">
                        {safeText(org.city)} • {safeText(org.state)} • {safeText(org.country)}
                      </Text>
                      <Text type="secondary">{safeText(org.address)}</Text>
                    </Space>
                  ) : (
                    "—"
                  )}
                </Descriptions.Item>
              </Descriptions>
            </div>
            <div className="generalStyles__mrFix mb-3">
              <DrawerRowDivider icon={faBook} title="Adapter" />
              <Descriptions bordered size="small" column={1} labelStyle={{ width: 170 }}>
                <Descriptions.Item label="ID">
                  <Space>
                    <ApiOutlined />
                    {safeText(adapter?.id)}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Name">
                  <Space>
                    <ApiOutlined />
                    {safeText(adapter?.name)}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Auth type">
                  {safeText(adapter?.authType)}
                </Descriptions.Item>
                <Descriptions.Item label="Type">{safeText(adapter?.type)}</Descriptions.Item>
                <Descriptions.Item label="Protocol type">
                  {safeText(adapter?.protocolType)}
                </Descriptions.Item>
                <Descriptions.Item label="Protocol ID">
                  {safeText(adapter?.protocolId)}
                </Descriptions.Item>
                <Descriptions.Item label="Leading zeroes ID">
                  {safeText(adapter?.leadingZeroesId)}
                </Descriptions.Item>
                <Descriptions.Item label="Lift type">
                  {safeText(adapter?.liftType)}
                </Descriptions.Item>
                <Descriptions.Item label="Total bacnet channels">
                  {safeText(adapter?.totalBacnetChannels)}
                </Descriptions.Item>
              </Descriptions>
            </div>
            {bank?.lifts && (
              <div className="generalStyles__mrFix mb-3">
                <DrawerRowDivider icon={faBook} title="Lifts" />
                <Descriptions bordered size="small" column={1} labelStyle={{ width: 170 }}>
                  {bank.lifts.map((lift) => (
                    <>
                      <Descriptions.Item
                        className="generalStyles__textEllipsis"
                        label={lift?.name || "--"}
                      >
                        <Space>
                          <ApiOutlined />
                          {safeText(lift?.id || "--")}
                        </Space>
                      </Descriptions.Item>
                    </>
                  ))}
                </Descriptions>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Card>
  );
}
